import {
  useChangeStatusDeviceMutation,
  useCreateOrUpdateDeviceMutation,
  useGetAllDeviceQuery,
  useGetDevicesByUserIdQuery,
  useGetOnlineSessionsQuery,
} from "../../services/device";
import {
  Col,
  Input,
  Row,
  Table,
  Form,
  Select,
  Button,
  Space,
  Dropdown,
  Tag,
  message,
} from "antd";
import { SearchOutlined } from "@ant-design/icons";
import GenericDrawer from "../drawer";
import { useEffect, useState } from "react";
import { Device } from "../../types/device";
import { OnlineSession } from "../../types/onlineSession";
import DeviceForm from "./forms/createUpdateDeviceForm";
import GenericModal from "../modal";
import { useSelector } from "react-redux";
import { RootState } from "../../redux/store";
import StatusDot from "../status-dot";
import MonoCell from "../mono-cell";

const { Option } = Select;

const DeviceManagementContent = () => {
  // const { data: devices, error, isLoading } = useGetAllDeviceQuery();
  const [form] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();
  const [devices, setDevices] = useState<any[]>([]);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const user = useSelector((state: RootState) => state.user.user);

  const { data: allDevicesResponse } = useGetAllDeviceQuery(
    { page, limit },
    { skip: user?.role !== 3 }
  );

  const { data: userDevices } = useGetDevicesByUserIdQuery(user?._id ?? "", {
    skip: !user?._id || user?.role !== 1,
  });

  const [createOrUpdateDevice, { data: createOrUpdateDeviceData }] =
    useCreateOrUpdateDeviceMutation();

  const [changeStatusDevice, { data: changeStatusDeviceData }] =
    useChangeStatusDeviceMutation();

  const [isDrawerVisible, setIsDrawerVisible] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState<Device | null>(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [deviceId, setDeviceId] = useState<string | null>(null);

  const openDrawer = (record: Device | null) => {
    setSelectedDevice(record);
    setIsDrawerVisible(true);
  };

  const closeDrawer = () => {
    setIsDrawerVisible(false);
    form.resetFields();
  };

  const closeModal = () => {
    setIsModalVisible(false);
  };

  const openModal = (record: string | null) => {
    setDeviceId(record);
    setIsModalVisible(true);
  };

  const handleSubmit = async (values: Device) => {
    values._id = selectedDevice?._id!;
    values.status = selectedDevice?.status ?? 0;
    await createOrUpdateDevice(values);
    closeDrawer();
  };

  const statusMenu = (record: Device) => ({
    items: [
      {
        key: "2",
        label: "Active",
        onClick: () => handleStatusChange(record._id, 2),
      },
      {
        key: "1",
        label: "Not Active",
        onClick: () => handleStatusChange(record._id, 1),
      },
    ],
  });

  const handleStatusChange = (id: any, newStatus: number) => {
    changeStatusDevice({ id, newStatus });
  };

  useEffect(() => {
    if (user?.role === 3 && allDevicesResponse?.data) {
      setDevices(allDevicesResponse.data);
    } else if (user?.role === 1 && userDevices) {
      setDevices(userDevices);
    }
  }, [user, allDevicesResponse, userDevices]);

  useEffect(() => {
    if (createOrUpdateDeviceData) {
      messageApi.success("Thay mới thiết bị thành công");
    }
  }, [createOrUpdateDeviceData, messageApi]);

  useEffect(() => {
    if (changeStatusDeviceData) {
      messageApi.success("Thay đổi status thành công");
    }
  }, [changeStatusDeviceData, messageApi]);

  interface ColumnOnlineHistory {
    title: string;
    dataIndex?: string;
    key: string;
    render?: (
      text: any,
      record: OnlineSession,
      index: number
    ) => React.ReactNode;
    width?: number;
  }

  const columnOnlineHistory: ColumnOnlineHistory[] = [
    {
      title: "STT",
      key: "stt",
      render: (_: any, __: OnlineSession, index: number) => (
        <span style={{ color: index === 0 ? "green" : undefined }}>
          {index + 1}
        </span>
      ),
      width: 70,
    },
    {
      title: "Thời gian bắt đầu online",
      dataIndex: "start_online",
      key: "start_online",
      render: (text, record, index) => (
        <span style={{ color: index === 0 ? "green" : undefined }}>
          {new Date(text).toLocaleString()}
        </span>
      ),
    },
    {
      title: "Thời gian cuối cùng online",
      dataIndex: "last_online",
      key: "last_online",
      render: (text, record, index) => {
        if (text === null) {
          return (
            <span style={{ color: index === 0 ? "green" : undefined }}>
              Thiết bị đang ONLINE
            </span>
          );
        } else {
          return (
            <span style={{ color: index === 0 ? "green" : undefined }}>
              {new Date(text).toLocaleString()}
            </span>
          );
        }
      },
    },
  ];

  const columns = [
    {
      title: "Hardware ID",
      dataIndex: "hardware_id",
      key: "hardware_id",
      render: (text: string) => <MonoCell>{text}</MonoCell>,
    },
    {
      title: "Tên thiết bị",
      dataIndex: "device_name",
      key: "device_name",
    },
    {
      title: "Trạng thái Online",
      dataIndex: "isOnline",
      key: "isOnline",
      render: (isOnline: boolean) => (
        <StatusDot state={isOnline ? "online" : "offline"} label={isOnline ? "Online" : "Offline"} />
      ),
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (text: string) => new Date(text).toLocaleString(),
    },
    {
      title: "Last Updated",
      dataIndex: "updatedAt",
      key: "updatedAt",
      render: (text: string) => new Date(text).toLocaleString(),
    },
    {
      title: "Chủ sở hữu",
      dataIndex: "owner",
      key: "owner",
      render: (text: string) => <p>{text}</p>,
    },
    ...(user?.role === 3
      ? [
          {
            title: "Trạng thái Active",
            dataIndex: "status",
            key: "status",
            render: (status: number, record: Device) => (
              <Dropdown menu={statusMenu(record)} trigger={["click"]}>
                <Tag
                  color={status === 2 ? "green" : "red"}
                  style={{ cursor: "pointer" }}
                >
                  {status === 2 ? "Active" : "Not Active"}
                </Tag>
              </Dropdown>
            ),
          },
        ]
      : []),
    {
      title: "",
      key: "action",
      render: (record: Device) => (
        <Space size="middle">
          <Button onClick={() => openDrawer(record)}>Cập nhật</Button>
          <Button onClick={() => openModal(record._id)}>
            Xem lịch sử online
          </Button>
        </Space>
      ),
    },
  ];

  const { data: onlineSessions } = useGetOnlineSessionsQuery(deviceId, {
    skip: !isModalVisible,
  });

  return (
    <div className="container" style={{ padding: "0 16px" }}>
      {contextHolder}
      <GenericModal
        width={600}
        visible={isModalVisible}
        onClose={closeModal}
        title={"Xem lịch sử online"}
        content={
          <Table
            columns={columnOnlineHistory}
            dataSource={onlineSessions}
            pagination={false}
            rowKey="_id"
            scroll={{ x: "max-content" }}
          />
        }
        footer={<></>}
      />

      <div className="page-head">
        <h1>Quản lý thiết bị</h1>
        {user && user.role !== 1 ? (
          <Button type="primary" onClick={() => openDrawer(null)}>
            Thêm thiết bị
          </Button>
        ) : (
          <></>
        )}
      </div>

      <GenericDrawer
        width={500}
        visible={isDrawerVisible}
        onClose={closeDrawer}
        title="Thêm thiết bị"
        formComponent={DeviceForm}
        formProps={{
          initialValues: selectedDevice,
          onSubmit: handleSubmit,
          form: form,
        }}
      />


      <Table
        columns={columns}
        dataSource={devices}
        rowKey="_id"
        scroll={{ x: "max-content" }}
        pagination={{
          current: page,
          pageSize: limit,
          total: allDevicesResponse?.pagination?.total || 0,
          onChange: (newPage, newLimit) => {
            setPage(newPage);
            setLimit(newLimit);
          },
          showSizeChanger: true,
          pageSizeOptions: ["5", "10", "20", "50"],
        }}
      />
    </div>
  );
};

export default DeviceManagementContent;
